import { useState } from "react";
import { Building2, ChevronLeft, ChevronRight, Pause, Play, SkipForward, Sparkles } from "lucide-react";
import { ARTIST, TRACKS, next, toggleCity, toggleMusic, toggleSfx, useAudio } from "./index";

const KEY = "xf_deck";
const CSS = `
@keyframes xf-eq { 0%, 100% { transform: scaleY(.3) } 50% { transform: scaleY(1) } }
@keyframes xf-scroll { from { transform: translateX(0) } to { transform: translateX(-50%) } }
.xf-eq span { transform-origin: bottom; animation: xf-eq .8s ease-in-out infinite }
.xf-eq span:nth-child(2) { animation-delay: -.3s } .xf-eq span:nth-child(3) { animation-delay: -.55s }
.xf-title { animation: xf-scroll 12s linear infinite }
.xf-deck-body { display: grid; grid-template-columns: 0fr; transition: grid-template-columns .25s ease-out }
.xf-deck-body[data-open=true] { grid-template-columns: 1fr }
@media (prefers-reduced-motion: reduce) { .xf-eq span, .xf-title { animation: none } .xf-deck-body { transition: none } }
`;

const Round = ({ className = "", ...p }) => (
  <button type="button" {...p} className={`grid h-8 w-8 shrink-0 place-items-center rounded-full transition active:scale-90 ${className}`} />
);

const Toggle = ({ on, label, ...p }) => (
  <Round aria-label={label} aria-pressed={on} title={label} {...p}
    className={on ? "bg-x-accent-soft text-x-accent-ink" : "text-x-mute hover:bg-x-raised hover:text-x-ink"} />
);

// The soundtrack and the city's sounds. Folded, it's just play/pause; open, it adds the track, skip and the switches
// for the city and the HUD sounds. It remembers which way you left it.
export function SoundDeck({ compact = false }) {
  const a = useAudio();
  const [open, setOpen] = useState(() => (localStorage.getItem(KEY) ?? (compact ? "0" : "1")) === "1");
  const fold = () => { localStorage.setItem(KEY, open ? "0" : "1"); setOpen(!open); };
  const title = `${ARTIST} · ${TRACKS[a.track].title}`;
  return (
    <div className="hud-panel pointer-events-auto flex items-center gap-1 rounded-full p-1" data-testid="sound-deck" data-open={open}>
      <style>{CSS}</style>
      <button type="button" onClick={toggleMusic} aria-label={a.playing ? "Pause music" : "Play music"} title={title} data-testid="music-toggle"
        className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-x-accent text-white transition-transform active:scale-90">
        {a.playing ? <Pause className="h-4 w-4" fill="currentColor" /> : <Play className="h-4 w-4 translate-x-px" fill="currentColor" />}
      </button>
      {a.playing && (
        <span className="xf-eq flex h-4 shrink-0 items-end gap-0.5 px-0.5" aria-hidden>
          {[0, 1, 2].map((i) => <span key={i} className="block h-full w-1 rounded-full bg-x-accent" />)}
        </span>
      )}
      <div className="xf-deck-body" data-open={open} aria-hidden={!open}>
        <div className="flex min-w-0 items-center gap-1 overflow-hidden">
          {!compact && (
            <span className="w-32 shrink-0 overflow-hidden whitespace-nowrap text-[11px] font-semibold text-x-mute" title={title}>
              <span className={`inline-block ${a.playing ? "xf-title" : ""}`}>
                {[0, 1].map((i) => <span key={i} className="pr-6">{title} ·</span>)}
              </span>
            </span>
          )}
          <Round onClick={next} tabIndex={open ? 0 : -1} aria-label="Next track" title="Next track" data-testid="music-next" className="text-x-mute hover:text-x-ink">
            <SkipForward className="h-4 w-4" />
          </Round>
          <Toggle on={a.city} onClick={toggleCity} tabIndex={open ? 0 : -1} label={a.city ? "City sounds on" : "City sounds off"} data-testid="city-toggle">
            <Building2 className="h-4 w-4" />
          </Toggle>
          <Toggle on={a.sfx} onClick={toggleSfx} tabIndex={open ? 0 : -1} label={a.sfx ? "HUD sounds on" : "HUD sounds off"} data-testid="sfx-toggle">
            <Sparkles className="h-4 w-4" />
          </Toggle>
        </div>
      </div>
      <Round onClick={fold} aria-label={open ? "Fold the sound controls" : "Open the sound controls"} aria-expanded={open} title={open ? "Fold" : "Sound"}
        data-testid="sound-deck-fold" className="h-7 w-6 text-x-mute hover:text-x-ink">
        {open ? <ChevronLeft className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
      </Round>
    </div>
  );
}
